import { applyFilters } from './applyFilters';
import {
  clearCache as clearLRUCache,
  clearCacheForResource as clearResourceCache,
  generateCacheKey,
  getCacheStats,
  lruCache
} from './cacheUtils';

function clearCache() {
  clearLRUCache();
}

function clearCacheForResource(resourceId) {
  clearResourceCache(resourceId);
}

async function processQueue(requestQueue, isProcessingQueueRef, datasourceConfig) {
  if (isProcessingQueueRef.current || requestQueue.length === 0) {
    return;
  }

  isProcessingQueueRef.current = true;

  while (requestQueue.length > 0) {
    const { params, resolve, reject } = requestQueue.shift();

    try {
      await executeRequest(params, datasourceConfig);
      resolve();
    } catch (error) {
      reject(error);
    }
  }

  isProcessingQueueRef.current = false;
  datasourceConfig.setLoading(false);
}

async function executeRequest(params, config) {
  const { startRow, endRow } = params;
  const {
    hasCustomData,
    hasServerData,
    customData,
    formatData,
    selectPreselectedNodes,
    queryFn,
    limit,
    sort,
    queryFilters,
    queryParams,
    queryKey,
    name,
    setFetchedData,
    setTotalCount,
    totalCountRef,
    skeletonSize
  } = config;

  const skeletonCount = Math.min(skeletonSize, endRow - startRow);
  const skeletonRows = Array.from({ length: skeletonCount }, (_, index) => ({
    id: `skeleton-${startRow + index}`,
    index: startRow + index,
    _isSkeleton: true
  }));

  params.successCallback(skeletonRows, -1);

  const pageSize =
    Number.isFinite(Number(limit)) && Number(limit) > 0
      ? Number(limit)
      : Math.max(1, endRow - startRow);

  if (hasCustomData && hasServerData) {
    const filteredCustomData = queryFilters ? applyFilters(customData, queryFilters) : customData;

    const prefixLen = filteredCustomData.length;

    if (endRow <= prefixLen) {
      const slice = filteredCustomData.slice(startRow, endRow);
      const data = formatData ? formatData(slice) : slice;
      params.successCallback(data, filteredCustomData.length);
      setTimeout(() => selectPreselectedNodes(), 0);
      return;
    }

    const customSlice = startRow < prefixLen ? filteredCustomData.slice(startRow, prefixLen) : [];

    const adjustedStart = Math.max(0, startRow - prefixLen);
    const adjustedEnd = Math.max(0, endRow - prefixLen);
    const firstPage = Math.floor(adjustedStart / pageSize) + 1;
    const lastPage = Math.floor(Math.max(adjustedEnd - 1, 0) / pageSize) + 1;

    const pagePromises = [];

    for (let page = firstPage; page <= lastPage; page += 1) {
      const queryParamsForPage = {
        resourceId: queryKey || name,
        limit: pageSize,
        page,
        sort,
        filters: queryFilters,
        withCount: true,
        ...queryParams
      };

      const cacheKey = generateCacheKey(queryParamsForPage);

      if (lruCache.has(cacheKey)) {
        pagePromises.push(Promise.resolve(lruCache.get(cacheKey)));
      } else {
        const apiPromise = queryFn(null, queryParamsForPage);
        apiPromise.then((response) => {
          lruCache.set(cacheKey, response);
        });
        pagePromises.push(apiPromise);
      }
    }

    const responses = await Promise.all(pagePromises);
    let allServerRows = [];
    let serverCount = null;

    responses.forEach((response, index) => {
      const rawData = response[queryKey || name] || [];
      if (setFetchedData && index === 0) setFetchedData(response);
      serverCount = Number.isFinite(Number(response?.count)) ? Number(response.count) : serverCount;
      allServerRows = allServerRows.concat(rawData);
    });

    const startOffsetInFirstPage = adjustedStart % limit;
    const neededLength = adjustedEnd - adjustedStart;
    const slicedServer = allServerRows.slice(
      startOffsetInFirstPage,
      startOffsetInFirstPage + neededLength
    );

    const formattedCustom = formatData ? formatData(customSlice) : customSlice;
    const formattedServer = formatData ? formatData(slicedServer) : slicedServer;

    const merged = [...formattedCustom, ...formattedServer];

    if (Number.isFinite(serverCount)) {
      const combinedCount = filteredCustomData.length + serverCount;
      setTotalCount(combinedCount);
      const lastRow = combinedCount <= endRow ? combinedCount : -1;

      if (setFetchedData) setFetchedData(merged);
      params.successCallback(merged, lastRow);
    } else {
      params.successCallback(merged, -1);
    }

    setTimeout(() => selectPreselectedNodes(), 0);
    return;
  }

  if (hasServerData) {
    const page = Math.floor(startRow / pageSize) + 1;

    const queryParamsForPage = {
      resourceId: queryKey || name,
      limit: pageSize,
      page,
      sort,
      filters: queryFilters,
      withCount: true,
      ...queryParams
    };

    const cacheKey = generateCacheKey(queryParamsForPage);

    let response;
    if (lruCache.has(cacheKey)) {
      response = lruCache.get(cacheKey);
    } else {
      response = await queryFn(null, queryParamsForPage);
      lruCache.set(cacheKey, response);
    }

    const rawData = response[queryKey || name] || [];
    const data = formatData ? formatData(rawData) : rawData;
    if (setFetchedData) setFetchedData(response);

    const rawCount = response?.count;
    const count = Number.isFinite(Number(rawCount))
      ? Number(rawCount)
      : totalCountRef?.current || endRow;
    setTotalCount(count);

    const lastRow = count <= endRow ? count : -1;
    params.successCallback(data, lastRow);
    setTimeout(() => selectPreselectedNodes(), 0);
    return;
  }

  params.successCallback([], 0);
}

export { clearCache, clearCacheForResource, getCacheStats };

export default function infiniteDatasource(config) {
  const { isInfinite } = config;

  if (!isInfinite) return null;

  const requestQueue = [];
  const isProcessingQueueRef = { current: false };

  return {
    getRows: async (params) => {
      return new Promise((resolve, reject) => {
        requestQueue.push({ params, resolve, reject });

        processQueue(requestQueue, isProcessingQueueRef, config).catch((error) => {
          console.error('Error processing queue:', error);
          params.failCallback();
        });
      });
    }
  };
}
